import React, {
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef
} from 'react';
import { makeStyles, Typography } from '@material-ui/core';

import BaseButton from '../../../Components/Buttons/BaseButton';
import TextInput from '../../../Components/Inputs/TextInput';
import ShowPassword from '../../../Components/Adornments/ShowPassword';

import { AlertsDispatchContext } from '../../../Contexts/AlertsContext';
import { UserDispatchContext } from '../../../Contexts/UserContext';

import { ROUTE_NAMES } from '../../../Routes/Routes';
import { LoginAPI } from '../../../API/UserAPI';
import { extractErrorMessage } from '../../../Utils/Errors/Errors';

import * as yup from 'yup';
import { history } from '../../../Routes/history';

import SocialLoginButtons from '../SocialLoginButtons';
import { updateUser } from '../../../Utils/User/Actions';
import ChangePasswordDialog from '../../../Components/PasswordChange/ChangePasswordDialog';
import PhoneVerificationDialog from '../../Profile/PhoneVerificationDialog';
import _get from 'lodash/get';
import { useCaptcha } from '../../../Hooks/useCaptcha';
import TurnstileManagedCaptcha from '../../../Components/Captcha/TurnstileManagedCaptcha';

const loginSchema = yup.object({
  password: yup.string().required('Ingresa una contraseña'),
  username: yup
    .string()
    .trim()
    .lowercase()
    .email('Debes ingresar un correo válido')
    .required('Ingresa un correo')
});

const LoginContentEFG = props => {
  const {
    closeDialog = null,
    initialUsername = '',
    hideSocialButtons = false
  } = props;

  const classes = useStyles();

  const isFirefox = window.navigator.userAgent.indexOf('Firefox') !== -1;

  const setCurrentUser = useContext(UserDispatchContext);
  const setAlert = useContext(AlertsDispatchContext);

  const [loading, setLoading] = useState(false);
  const [isFacebookLoading, setIsFacebookLoading] = useState(true);

  // FORM states
  const [canSubmit, setCanSubmit] = useState(false);
  const [passwordShown, setShowPassword] = useState(false);
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({
    username: null,
    password: null
  });
  const [loadingGoogle, setLoadingGoogle] = useState(false);

  const captchaWrapperRef = useRef(null);
  const {
    captchaRef,
    handlers: captchaHandlers,
    captchaToken,
    isValidCaptcha
  } = useCaptcha();

  const [showChangePassword, setShowChangePassword] = useState(false);

  const [showOtpVerification, setShowOtpVerification] = useState(false);

  const onChangeUsername = useCallback(event => {
    setUsername(event.target.value);
  }, []);

  const onChangePassword = useCallback(event => {
    setPassword(event.target.value);
  }, []);

  const togglePasswordShow = useCallback(() => {
    setShowPassword(!passwordShown);
  }, [passwordShown]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();
      setLoading(true);

      const handleLoginSuccess = (token, appToken, contractToAssociate) => {
        updateUser(setCurrentUser, token, appToken);
        if (!closeDialog) {
          if (contractToAssociate) {
            history.push(ROUTE_NAMES.associate, {
              contractId: contractToAssociate
            });

            return;
          }

          history.replace('/');
        }
      };

      const handlePasswordChangeRequired = () => {
        setAlert({
          type: 'warning',
          message: 'Tu contraseña ha vencido. Por seguridad, debes cambiarla.'
        });
        setShowOtpVerification(true);
        setLoading(false);
        return true;
      };

      try {
        const response = await LoginAPI(username, password, captchaToken);

        captchaHandlers.reset();

        if (response.success) {
          const {
            data: token,
            dataApp: appToken,
            contractToAssociate
          } = response.data;

          handleLoginSuccess(token, appToken, contractToAssociate);
          return;
        }

        if (_get(response, 'error.status') === 403) {
          const isLocked = _get(
            response,
            'error.response.data.isLocked',
            false
          );

          if (isLocked) {
            setAlert({
              type: 'error',
              message:
                'El usuario fue bloqueado por límite de intentos fallidos. Revisa tu correo o contacta a soporte.'
            });
            setLoading(false);
            return;
          }

          const handled = handlePasswordChangeRequired(
            _get(response, 'error.response.data')
          );
          if (handled) {
            return;
          }
        }

        const message = extractErrorMessage(response);
        if (message.key === 'recaptcha') {
          setAlert({
            type: 'error',
            message: message.message
          });
          setLoading(false);
          return;
        }
        if (message.message === 'No has validado tu correo') {
          history.push(ROUTE_NAMES.confirmEmail, { email: username });
          setLoading(false);
          return;
        }

        setAlert({
          type: 'error',
          message: 'Email o contraseña incorrectos'
        });
        setLoading(false);
      } catch (error) {
        if (error.response && error.response.data) {
          const handled = handlePasswordChangeRequired(error.response.data);
          if (handled) {
            return;
          }
        }

        setAlert({
          type: 'error',
          message: 'Error al procesar la solicitud'
        });
        setLoading(false);
      }
    },
    [
      username,
      password,
      setCurrentUser,
      setAlert,
      closeDialog,
      captchaHandlers.reset,
      captchaToken
    ]
  );

  const redirectToRegister = () => {
    if (closeDialog) {
      closeDialog();
    }
    history.push(ROUTE_NAMES.register);
  };

  const handleForgotPassword = () => {
    if (closeDialog) {
      closeDialog();
    }
    history.push(ROUTE_NAMES.forgot, { username });
  };

  useEffect(() => {
    if (!username) {
      setCanSubmit(false);
      return;
    }

    try {
      loginSchema.validateSync({ username, password });
      setErrors({});
      setCanSubmit(true);
    } catch (err) {
      setCanSubmit(false);
      setErrors({
        [err.path]: err.errors[0]
      });
    }
  }, [username, password]);

  useEffect(() => {
    if (isFacebookLoading) {
      /* Disable button when user is in Firefox Incognito mode
      because Facebook login doesn't work */
      if (isFirefox) {
        const disableFirefox = setTimeout(
          () => setIsFacebookLoading(false),
          20000
        );
        return () => {
          clearTimeout(disableFirefox);
        };
      }
    }
  }, [isFacebookLoading, setIsFacebookLoading, isFirefox]);

  return (
    <>
      <div className={classes.formContainer}>
        <Typography className={classes.title}>Iniciar sesión</Typography>
        <SocialLoginButtons
          loading={loading}
          closeDialog={closeDialog}
          hideSocialButtons={hideSocialButtons}
          loadingGoogle={loadingGoogle}
          setLoadingGoogle={setLoadingGoogle}
          hideAppleButton
        />
        <form className={classes.form} onSubmit={handleSubmit}>
          <TextInput
            value={username}
            onChange={onChangeUsername}
            id="email"
            label="Correo electrónico"
            type="email"
            error={Boolean(errors.username)}
            helperText={errors.username}
            required={true}
            fullWidth
          />
          <TextInput
            value={password}
            onChange={onChangePassword}
            margin="none"
            className={classes.passwordField}
            id="password"
            label="Contraseña"
            type={passwordShown ? 'text' : 'password'}
            required={true}
            error={Boolean(errors.password)}
            helperText={errors.password}
            fullWidth
            InputProps={{
              endAdornment: (
                <ShowPassword
                  togglePasswordShow={togglePasswordShow}
                  passwordShown={passwordShown}
                />
              )
            }}
          />

          <Typography
            className={classes.forgotPassword}
            onClick={handleForgotPassword}
          >
            ¿Olvidaste tu contraseña?
          </Typography>

          <div ref={captchaWrapperRef} className={classes.captchaWrapper}>
            <TurnstileManagedCaptcha
              captchaRef={captchaRef}
              referenceEl={captchaWrapperRef}
              onResolved={captchaHandlers.onResolved}
              onExpired={captchaHandlers.onExpired}
              onError={captchaHandlers.onError}
              onLoaded={captchaHandlers.onLoaded}
              locale="es"
            />
          </div>

          <BaseButton
            loading={loading}
            progress={loading}
            className={classes.button}
            disabled={!canSubmit || loadingGoogle || !isValidCaptcha}
            fullWidth
            id="login"
            type="submit"
            color="primary"
            variant="contained"
          >
            Iniciar sesión
          </BaseButton>

          <Typography className={classes.bottomText}>
            ¿No tienes cuenta?{' '}
            <span
              className={classes.signUp}
              onClick={
                !loadingGoogle && !loading ? redirectToRegister : undefined
              }
            >
              Regístrate
            </span>
          </Typography>
        </form>
      </div>
      {showOtpVerification && (
        <PhoneVerificationDialog
          open={showOtpVerification}
          setDialog={setShowOtpVerification}
          setAlert={setAlert}
          title="Código de verificación para cambio de contraseña"
          isAlphanumeric={true}
          codeLength={6}
          formEmail={username}
          successCallback={() => {
            setShowOtpVerification(false);
            setShowChangePassword(true);
          }}
        />
      )}
      {showChangePassword && (
        <ChangePasswordDialog
          open={showChangePassword}
          onClose={() => setShowChangePassword(false)}
          setAlert={setAlert}
          email={username}
        />
      )}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  formContainer: {
    maxWidth: '80%',
    height: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexDirection: 'column'
  },
  title: {
    marginTop: 40,
    marginBottom: 20,
    fontSize: 24
  },
  passwordField: {
    marginTop: theme.spacing(2)
  },
  forgotPassword: {
    display: 'flex',
    justifyContent: 'flex-end',
    cursor: 'pointer',
    color: theme.palette.text.default,
    paddingTop: 8,
    '&:hover': {
      color: theme.palette.text.main
    }
  },
  button: {
    marginTop: theme.spacing(4)
  },
  form: {
    maxWidth: '100%'
  },
  bottomText: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontSize: 14,
    textAlign: 'center'
  },
  signUp: {
    fontSize: 14,
    color: theme.palette.primary.dark,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main
    }
  },
  captchaWrapper: {
    width: '100%',
    overflow: 'hidden'
  }
}));

export default LoginContentEFG;
